import { createSlice } from "@reduxjs/toolkit";
import {
  addPart,
  addPartsBulk,
  deletePart,
  fetchFeaturedInventory,
  fetchPartById,
  fetchPartCategories,
  fetchSellerInventory,
  updateFeaturePart
} from "./usePartsThunk";

const initialState = {
  items: [],
  partsLoading: false,
  error: null,
  // Regular inventory
  inventory: [],
  inventoryLoading: false,
  inventoryError: null,
  hasMore: true,
  paginationLoading: false,
  lastDoc: null,
  pageSize: 10,
  // Featured inventory
  featuredInventory: [],
  featuredLoading: false,
  featuredError: null,
  featuredHasMore: true,
  featuredPaginationLoading: false,
  featuredLastDoc: null,
  bulkLoading: false,
  // Misc
  selectedPart: null,
  deleteLoading: false,
  categories: [],
  categoriesLoading: false,
  updateFeatureLoading: false
};

const usePartsSlice = createSlice({
  name: "parts",
  initialState,
  reducers: {
    setInventory: (state, action) => {
      state.inventory = action.payload;
    },
    setInventoryLoading: (state, action) => {
      state.inventoryLoading = action.payload;
    },
    clearInventory: (state) => {
      state.inventory = [];
      state.inventoryLoading = false;
      state.inventoryError = null;
      state.hasMore = true;
      state.lastDoc = null;
      state.paginationLoading = false;
    },
    resetPagination: (state) => {
      state.inventory = [];
      state.hasMore = true;
      state.lastDoc = null;
      state.paginationLoading = false;
      // Also reset featured pagination
      state.featuredInventory = [];
      state.featuredHasMore = true;
      state.featuredLastDoc = null;
      state.featuredPaginationLoading = false;
    },
    clearSelectedPart: (state) => {
      state.selectedPart = null;
    },
    // Featured inventory resets
    clearFeaturedInventory: (state) => {
      state.featuredInventory = [];
      state.featuredLoading = false;
      state.featuredError = null;
      state.featuredHasMore = true;
      state.featuredLastDoc = null;
      state.featuredPaginationLoading = false;
    },
    appendInventory: (state, action) => {
      state.inventory = [...state.inventory, ...action.payload];
    },
    setHasMore: (state, action) => {
      state.hasMore = action.payload;
    },
    setPaginationLoading: (state, action) => {
      state.paginationLoading = action.payload;
    },
    setLastDoc: (state, action) => {
      state.lastDoc = action.payload;
    },
    setFeaturedLastDoc: (state, action) => {
      state.featuredLastDoc = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(addPart.pending, (state) => {
        state.partsLoading = true;
        state.error = null;
      })
      .addCase(addPart.fulfilled, (state, action) => {
        state.partsLoading = false;
        state.items.push(action.payload);
      })
      .addCase(addPart.rejected, (state, action) => {
        state.partsLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchPartById.pending, (state) => {
        state.fetchLoading = true;
        state.error = null;
      })
      .addCase(fetchPartById.fulfilled, (state, action) => {
        state.fetchLoading = false;
        state.selectedPart = action.payload;
      })
      .addCase(fetchPartById.rejected, (state, action) => {
        state.fetchLoading = false;
        state.error = action.payload || "Failed to fetch part";
      })
      .addCase(deletePart.pending, (state) => {
        state.deleteLoading = true;
      })
      .addCase(deletePart.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.inventory = state.inventory.filter(
          (item) => item.id !== action.payload.id
        );
      })
      .addCase(deletePart.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload || "Failed to delete part";
      })
      .addCase(fetchSellerInventory.pending, (state, action) => {
        const { loadMore = false } = action.meta.arg;
        if (loadMore) {
          state.paginationLoading = true; // load more case
        } else {
          state.inventoryLoading = true; // initial case
        }
        state.inventoryError = null;
      })
      .addCase(fetchSellerInventory.fulfilled, (state, action) => {
        state.inventoryLoading = false;
        state.paginationLoading = false;
        const { parts, lastDoc, hasMore, isInitial } = action.payload;

        if (isInitial) {
          state.inventory = parts;
        } else {
          state.inventory = [...state.inventory, ...parts];
        }

        state.lastDoc = lastDoc;
        state.hasMore = hasMore;
      })
      .addCase(fetchSellerInventory.rejected, (state, action) => {
        state.inventoryLoading = false;
        state.paginationLoading = false;
        state.inventoryError = action.payload || "Failed to fetch inventory";
      })
      .addCase(fetchPartCategories.pending, (state) => {
        state.categoriesLoading = true;
      })
      .addCase(fetchPartCategories.fulfilled, (state, action) => {
        state.categoriesLoading = false;
        state.categories = action.payload;
      })
      .addCase(fetchPartCategories.rejected, (state, action) => {
        state.categoriesLoading = false;
      })
      .addCase(addPartsBulk.pending, (state) => {
        state.bulkLoading = true;
      })
      .addCase(addPartsBulk.fulfilled, (state, action) => {
        state.bulkLoading = false;
      })
      .addCase(addPartsBulk.rejected, (state, action) => {
        state.bulkLoading = false;
      })
      //  fetchFeaturedInventory
      .addCase(fetchFeaturedInventory.pending, (state, action) => {
        const { loadMore = false } = action.meta.arg;
        if (loadMore) {
          state.featuredPaginationLoading = true; // load more case
        } else {
          state.featuredLoading = true; // initial case
        }
        state.featuredError = null;
      })
      .addCase(fetchFeaturedInventory.fulfilled, (state, action) => {
        state.featuredPaginationLoading = false;
        state.featuredLoading = false;
        const { featuredParts, lastDoc, hasMore, isInitial } = action.payload;

        if (isInitial) {
          state.featuredInventory = featuredParts;
        } else {
          state.featuredInventory = [
            ...state.featuredInventory,
            ...featuredParts
          ];
        }

        state.featuredLastDoc = lastDoc;
        state.featuredHasMore = hasMore;
      })
      .addCase(fetchFeaturedInventory.rejected, (state, action) => {
        state.featuredPaginationLoading = false;
        state.featuredLoading = false;
        state.featuredError =
          action.payload || "Failed to fetch featured inventory";
      })
      // Update Feature Part
      .addCase(updateFeaturePart.pending, (state) => {
        state.updateFeatureLoading = true;
      })
      .addCase(updateFeaturePart.fulfilled, (state, action) => {
        state.updateFeatureLoading = false;
      })
      .addCase(updateFeaturePart.rejected, (state, action) => {
        state.updateFeatureLoading = false;
        state.error = action.payload || "Failed to update featured part";
      });
  }
});

export default usePartsSlice.reducer;
export const {
  setInventory,
  setInventoryLoading,
  clearInventory,
  appendInventory,
  setHasMore,
  setPaginationLoading,
  clearSelectedPart,
  setLastDoc,
  resetPagination
} = usePartsSlice.actions;
